import {GridModel} from '@xh/hoist/cmp/grid';
import {HoistModel, TaskObserver, XH, managed} from '@xh/hoist/core';
import {action, bindable, observable, runInAction} from '@xh/hoist/mobx';
import {Security} from '../../data/DataTypes';
import {itemRenderer} from './SecurityPicker';
import {Timer} from '@xh/hoist/utils/async';
import {createRef, SyntheticEvent} from 'react';
import {escapeRegex} from '../../data/Utils';

export class SecurityPickerModel extends HoistModel {
    apiTask = TaskObserver.trackLast();
    @observable accessor isOpen: boolean = false;
    @bindable accessor query: string = '';
    @managed securityGrid = this.createGridModel();
    inputRef = createRef<HTMLInputElement>();
    @managed private readonly focusTimer: Timer = Timer.create({
        interval: -1,
        runFn: () => this.inputRef?.current?.focus()
    });

    override onLinked(): void {
        if (this.componentProps.ticker) this.query = this.componentProps.ticker;

        // componentProps change while the picker renders - defer, so the query input isn't updated mid-render
        this.addReaction({
            track: () => this.componentProps.ticker,
            run: ticker => (this.query = ticker),
            debounce: 0
        });

        this.addReaction({
            track: () => this.query,
            run: () => this.onQuery().linkTo(this.apiTask),
            debounce: {interval: 250}
        });

        this.addReaction({
            track: () => this.isOpen,
            run: () => this.componentProps.onOpen?.(this.isOpen)
        });
    }

    @action
    open(ticker: string) {
        this.query = ticker;
        this.isOpen = true;
        this.onQuery();
        this.focusTimer.setInterval(500);
    }

    @action
    close() {
        this.isOpen = false;
        this.securityGrid.clear();
        this.focusTimer.setInterval(-1);
    }

    @action
    onInteraction(nextState: boolean, e: SyntheticEvent<HTMLElement>) {
        if (nextState) {
            if (this.componentProps.disabled) return;

            this.isOpen = true;
            this.onQuery();
        } else if (e) {
            if (e.type == 'keydown') return;
            if (e.type == 'click') {
                //@ts-ignore
                if (e.target.className == 'bp6-input') {
                    this.inputRef?.current?.focus();
                    return;
                }
            } else return;
        } else {
            this.close();
        }
    }

    onSearchKeyDown(e: KeyboardEvent) {
        let shouldSuppress = false;
        if (e.key == 'Escape') {
            if (this.isOpen) {
                this.close();
            }
            shouldSuppress = true;
        } else if (e.key == 'ArrowDown') {
            this.scroll(true);
            shouldSuppress = true;
        } else if (e.key == 'ArrowUp') {
            this.scroll(false);
            shouldSuppress = true;
        } else if (e.key == 'Enter') {
            if (this.componentProps.disabled) return;
            const security = this.securityGrid.selectedRecord?.raw as Security;
            if (security) this.select(security);
            else {
                runInAction(() => (this.isOpen = true));
                this.onQuery();
            }
            shouldSuppress = true;
        } else {
            if (!this.isOpen) {
                runInAction(() => (this.isOpen = true));
                this.onQuery();
            }
        }

        if (shouldSuppress) {
            e.preventDefault();
            e.stopPropagation();
        }
    }

    private scroll(down: boolean) {
        const selectedRecordId = this.securityGrid.selectedId,
            records = this.securityGrid.store.allRecords,
            rowCount = records.length;
        let idx = -1;

        if (!rowCount) return;

        if (!selectedRecordId) {
            this.securityGrid.selectFirstAsync();
            return;
        }

        records.forEach((r, i) => {
            if (r.id == selectedRecordId) {
                idx = i;
            }
        });

        if (idx < 0) return;

        if (down) {
            idx++;
            if (idx >= rowCount) idx = 0;
        } else {
            idx--;
            if (idx < 0) idx = rowCount - 1;
        }

        this.securityGrid.selectAsync(records[idx].id);
    }

    private async onQuery() {
        const query = this.query;

        if (!query) {
            this.securityGrid.clear();
            return;
        }

        const securities = await XH.secMasterService.searchSecuritiesAsync(query);
        const searchMask = query,
            regEx = new RegExp(escapeRegex(searchMask), 'ig'),
            replaceMask = `<span class="ticker-picker__highlight">${query.toUpperCase()}</span>`;
        const displayData = securities.map(sec => {
            return {
                ...sec,
                displayTicker: sec.ticker.replace(regEx, replaceMask),
                displayName: sec.name?.replace(regEx, replaceMask)
            };
        });
        this.securityGrid.clear();
        this.securityGrid.loadData(displayData);
    }

    @action
    private select(security: Security) {
        this.isOpen = false;
        this.focusTimer.setInterval(-1);

        this.componentProps.onSecuritySelected?.(security);
        if (!this.componentProps.keepSelection) this.query = '';
        else {
            this.query = security.ticker;
        }
    }

    private createGridModel(): GridModel {
        return new GridModel({
            contextMenu: null,
            onRowClicked: ({data: record}) => this.select(record.raw),
            showHover: true,
            stripeRows: true,
            emptyText: 'No Matches',
            rowBorders: true,
            sortBy: 'ticker',
            hideHeaders: true,
            columns: [
                {field: 'id', hidden: true},
                {field: 'ticker', hidden: true},
                {
                    field: 'name',
                    flex: 1,
                    rowHeight: 55,
                    renderer: (v, {record}) => itemRenderer({model: this, record})
                }
            ]
        });
    }
}
